import { startTransition } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App.tsx";
import "./index.css";
import { detectLang, loadLocale } from "./i18n/detect";
import { LocaleProvider, applyPack } from "./i18n/provider";
import type { LocalePack } from "./i18n/types";

// Error monitoring is loaded lazily (see below), so buffer anything thrown
// before it exists: locale pack load, the initial mount, early interactions.
const earlyErrors: unknown[] = [];
const onEarlyError = (e: ErrorEvent) => {
  earlyErrors.push(e.error ?? new Error(e.message));
};
const onEarlyRejection = (e: PromiseRejectionEvent) => {
  earlyErrors.push(e.reason);
};
window.addEventListener("error", onEarlyError);
window.addEventListener("unhandledrejection", onEarlyRejection);

const lang = detectLang();
const container = document.getElementById("root")!;

// LocaleProvider owns <html lang/dir>; it starts in English and switches when
// a pack is applied, so a prerendered English page never flips to RTL before
// the Arabic copy is actually rendered.
const tree = (pack?: LocalePack) => (
  <LocaleProvider initialPack={pack}>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </LocaleProvider>
);

// Non-English translations live in their own chunk (preloaded from index.html).
const packPromise = loadLocale(lang).then((pack) => {
  if (pack.lang !== lang) {
    // The chunk for the detected language failed to load; the page stays in
    // English, and the failure is reported once monitoring is up.
    earlyErrors.push(new Error(`Locale pack "${lang}" failed to load, fell back to English`));
  }
  return pack;
});

if (container.hasAttribute("data-ssr")) {
  // The HTML is the build-time English render of this route: hydrate it as is
  // (so the hero that already painted is the LCP), then swap the visitor's pack
  // in as a transition once it has arrived.
  hydrateRoot(container, tree(), {
    onRecoverableError: (err) => {
      earlyErrors.push(err);
      console.error(err);
    },
  });
  packPromise.then(applyPack).catch((err) => earlyErrors.push(err));
} else {
  // SPA shell (every other route): wait for the pack so nothing flashes
  // English, then mount as a transition so the mount yields to taps.
  packPromise
    .then((pack) => {
      const root = createRoot(container);
      startTransition(() => root.render(tree(pack)));
    })
    .catch((err) => earlyErrors.push(err));
}

// Error monitoring (see src/monitoring.ts) is loaded once the page is idle so
// its code never competes with hydration or the visitor's first taps.
function loadErrorMonitoring() {
  import("./monitoring")
    .then((m) => {
      window.removeEventListener("error", onEarlyError);
      window.removeEventListener("unhandledrejection", onEarlyRejection);
      m.initErrorMonitoring(earlyErrors.splice(0));
    })
    .catch(() => {
      /* monitoring is best-effort */
    });
}

function scheduleErrorMonitoring() {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(loadErrorMonitoring, { timeout: 5000 });
  } else {
    setTimeout(loadErrorMonitoring, 3000);
  }
}

if (document.readyState === "complete") {
  scheduleErrorMonitoring();
} else {
  window.addEventListener("load", scheduleErrorMonitoring, { once: true });
}
